import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useCallback, useMemo, } from 'react';
import { useLocation } from 'react-router-dom';
const isBrowser = typeof window !== 'undefined';
let BProgress = null;
if (isBrowser) {
    import('@bprogress/core')
        .then(module => {
        const resolvedModule = module ?? null;
        BProgress = resolvedModule;
        if (resolvedModule?.configure) {
            try {
                resolvedModule.configure({
                    minimum: 0.1,
                    easing: 'ease',
                    speed: 200,
                    showSpinner: true,
                    spinnerPosition: 'top-right',
                    color: 'var(--color-icavi-500)',
                    height: 3,
                    zIndex: 9999,
                    template: `
              <div class="bar" role="bar">
                <div class="peg"></div>
              </div>
              <div class="spinner" role="spinner">
                <div class="spinner-icon"></div>
              </div>
            `,
                });
            }
            catch (error) {
                console.warn('Erro ao configurar BProgress:', error);
            }
        }
    })
        .catch(error => {
        console.warn('BProgress não pôde ser carregado:', error);
    });
}
const ProgressContext = createContext(null);
export const useProgressContext = () => {
    const context = useContext(ProgressContext);
    if (!context) {
        throw new Error('useProgressContext deve ser usado dentro de um ProgressProvider');
    }
    return context;
};
const ProgressProvider = ({ children }) => {
    const location = useLocation();
    const domCache = useRef({
        progressBar: null,
        progressPeg: null,
        progressSpinner: null,
        lastUpdate: 0,
    });
    const getDOMElements = useCallback(() => {
        if (!isBrowser) {
            return domCache.current;
        }
        const now = Date.now();
        const cache = domCache.current;
        if (!cache.progressBar || now - cache.lastUpdate > 1000) {
            cache.progressBar = document.querySelector('#bprogress .bar');
            cache.progressPeg = document.querySelector('#bprogress .peg');
            cache.progressSpinner = document.querySelector('#bprogress .spinner-icon');
            cache.lastUpdate = now;
        }
        return cache;
    }, []);
    const applyOrangeStyles = useCallback(() => {
        if (!isBrowser) {
            return;
        }
        requestAnimationFrame(() => {
            const { progressBar, progressPeg, progressSpinner } = getDOMElements();
            if (progressBar) {
                if (progressBar.getAttribute('data-orange') === 'true') {
                    return;
                }
                progressBar.classList.remove('blue', 'primary', 'bg-icavi-500', 'bg-icavi-600');
                progressBar.style.removeProperty('background-color');
                progressBar.style.removeProperty('background-image');
                progressBar.style.removeProperty('background');
                progressBar.setAttribute('data-orange', 'true');
                progressBar.style.setProperty('background', 'linear-gradient(90deg, var(--color-icavi-600), var(--color-icavi-700))', 'important');
                progressBar.style.setProperty('background-color', 'var(--color-icavi-600)', 'important');
                progressBar.style.setProperty('background-image', 'linear-gradient(90deg, var(--color-icavi-600), var(--color-icavi-700))', 'important');
                progressBar.style.setProperty('box-shadow', '0 2px 4px rgba(249, 115, 22, 0.3)', 'important');
                const currentCssText = progressBar.style.cssText;
                progressBar.style.cssText = `${currentCssText}; background: linear-gradient(90deg, var(--color-icavi-600), var(--color-icavi-700)) !important; background-color: var(--color-icavi-500) !important;`;
            }
            if (progressPeg) {
                progressPeg.style.setProperty('box-shadow', '0 0 10px var(--color-icavi-600), 0 0 5px var(--color-icavi-600)', 'important');
                progressPeg.style.setProperty('background', 'linear-gradient(90deg, var(--color-icavi-600), var(--color-icavi-700))', 'important');
            }
            if (progressSpinner) {
                progressSpinner.style.setProperty('border-top-color', 'var(--color-icavi-600)', 'important');
                progressSpinner.style.setProperty('border-left-color', 'var(--color-icavi-600)', 'important');
            }
        });
    }, [getDOMElements]);
    useEffect(() => {
        if (!isBrowser) {
            return;
        }
        const timeoutId = window.setTimeout(() => {
            if (BProgress?.render) {
                try {
                    BProgress.render();
                }
                catch (error) {
                    console.warn('Erro ao renderizar BProgress:', error);
                }
            }
            applyOrangeStyles();
            if (BProgress?.isStarted) {
                let animationId = null;
                const optimizedStyleCheck = () => {
                    try {
                        if (!BProgress?.isStarted || !BProgress.isStarted()) {
                            if (animationId) {
                                cancelAnimationFrame(animationId);
                            }
                            return;
                        }
                        const { progressBar } = getDOMElements();
                        if (progressBar && progressBar.getAttribute('data-orange') === 'true') {
                            if (animationId) {
                                cancelAnimationFrame(animationId);
                            }
                        }
                        else {
                            applyOrangeStyles();
                            animationId = requestAnimationFrame(optimizedStyleCheck);
                        }
                    }
                    catch (error) {
                        console.warn('Erro ao verificar status do BProgress:', error);
                    }
                };
                optimizedStyleCheck();
                return () => {
                    if (animationId) {
                        cancelAnimationFrame(animationId);
                    }
                };
            }
            return undefined;
        }, 100);
        return () => {
            window.clearTimeout(timeoutId);
            if (BProgress?.remove) {
                try {
                    BProgress.remove();
                }
                catch (error) {
                    console.warn('Erro ao remover BProgress:', error);
                }
            }
        };
    }, [applyOrangeStyles, getDOMElements]);
    useEffect(() => {
        if (!BProgress?.start) {
            return;
        }
        try {
            BProgress.start();
        }
        catch (error) {
            console.warn('Erro ao iniciar BProgress:', error);
        }
        const timer = window.setTimeout(() => {
            if (BProgress?.done) {
                try {
                    BProgress.done();
                }
                catch (error) {
                    console.warn('Erro ao finalizar BProgress:', error);
                }
            }
        }, 400);
        return () => {
            window.clearTimeout(timer);
        };
    }, [location.pathname]);
    const progressMethods = useMemo(() => ({
        start: () => {
            try {
                BProgress?.start?.();
                applyOrangeStyles();
            }
            catch (error) {
                console.warn('Erro ao executar start:', error);
            }
        },
        done: () => {
            try {
                BProgress?.done?.();
            }
            catch (error) {
                console.warn('Erro ao executar done:', error);
            }
        },
        set: (value) => {
            try {
                BProgress?.set?.(value);
                applyOrangeStyles();
            }
            catch (error) {
                console.warn('Erro ao executar set:', error);
            }
        },
        inc: (amount) => {
            try {
                BProgress?.inc?.(amount);
                applyOrangeStyles();
            }
            catch (error) {
                console.warn('Erro ao executar inc:', error);
            }
        },
        isStarted: () => {
            try {
                return BProgress?.isStarted?.() ?? false;
            }
            catch (error) {
                console.warn('Erro ao executar isStarted:', error);
                return false;
            }
        },
        configure: (options) => {
            try {
                BProgress?.configure?.(options);
            }
            catch (error) {
                console.warn('Erro ao executar configure:', error);
            }
        },
    }), [applyOrangeStyles]);
    return _jsx(ProgressContext.Provider, { value: progressMethods, children: children });
};
export { ProgressContext };
export default ProgressProvider;
